import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { tileFor } from './signup/docSets'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  adminApi,
  type AdminOrgDetailResponse,
  type AdminOrgGate,
  type AdminOrgRow,
  type AdminOrgSeats,
} from '../api/endpoints'
import { ApiError } from '../api/types'

/**
 * The ORGANIZATION lens of Users & KYC — the verification queue and the review
 * panel (step 8).
 *
 * ── WHY THIS SCREEN EXISTS ───────────────────────────────────────────────
 * `POST /admin/orgs/:orgId/kyc` has shipped since step 5 and had NO UI. Dad —
 * who is the KYC approver — literally could not approve a company. That is the
 * one grant path org-inherited verification has, so without this screen the
 * whole product decision seats are sold on ("approve the company once, every
 * member is productive immediately") was unreachable from the console.
 *
 * ── THE RULE THIS SCREEN IS BUILT AROUND ─────────────────────────────────
 * NEVER REPORT SUCCESS FOR A DECISION THE GATE IGNORES. The server tells us
 * exactly what will happen — `gate.inheritanceActive` (are the flags armed at
 * all), `membersInvalidated` (how many people just moved), and the 60s
 * propagation SLA — and every one of those is rendered. Approving a company
 * whose flags are unarmed prints "recorded, but no member's access changed",
 * not a green tick. Rejecting one says out loud how many people it locks and
 * that there is no per-person way back out.
 *
 * ── AND THE ONE ABOUT PROJECTION ─────────────────────────────────────────
 * Everything rendered here comes from the server's allowlist projection. There
 * is no supplier API key, no Stripe id and no invite token hash to render
 * because the payload does not contain one.
 */

/* ─────────────────────────────── primitives ──────────────────────────────── */

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${fmtDate(iso)} · ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
}

type KycStatus = AdminOrgRow['kycStatus']

const STATUS_STYLE: Record<KycStatus, { label: string; cls: string }> = {
  approved: { label: 'Verified', cls: 'bg-success/15 text-success' },
  submitted: { label: 'Awaiting review', cls: 'bg-warning/15 text-warning' },
  rejected: { label: 'Rejected', cls: 'bg-danger/10 text-danger' },
  not_submitted: { label: 'No documents', cls: 'bg-page text-soft' },
}

function StatusPill({ status }: { status: KycStatus }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.not_submitted
  return (
    <span className={`rounded-full text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  )
}

/**
 * The seat meter. A company's seat position is four numbers that only mean
 * something together (occupied / free / reserved by an outstanding invite /
 * over the limit), and four numbers in a row is a database row, not an answer.
 * One bar answers "can they add anyone" at a glance.
 */
function SeatMeter({ seats, compact = false }: { seats: AdminOrgSeats; compact?: boolean }) {
  const reduce = useReducedMotion()
  const limit = Math.max(seats.limit, 1)
  const over = Math.max(0, seats.used - seats.limit)
  const used = Math.min(seats.used, seats.limit)
  const pending = Math.min(seats.pendingInvites, Math.max(0, seats.limit - used))
  const free = Math.max(0, seats.limit - used - pending)
  const seg = (n: number) => `${(n / limit) * 100}%`
  return (
    <div className={compact ? 'w-28' : 'w-full max-w-sm'}>
      <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-page" role="img"
        aria-label={`${seats.used} of ${seats.limit} seats used, ${seats.pendingInvites} invited`}>
        <motion.span className="bg-navy-900" initial={reduce ? false : { width: 0 }} animate={{ width: seg(used) }}
          transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }} />
        <motion.span className="bg-gold-300" initial={reduce ? false : { width: 0 }} animate={{ width: seg(pending) }}
          transition={{ duration: 0.42, delay: 0.06, ease: [0.16, 1, 0.3, 1] }} />
        <span style={{ width: seg(free) }} />
        {over > 0 && <span className="bg-danger" style={{ width: seg(over) }} />}
      </div>
      {!compact && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted">
          <span className="tabular"><b className="text-ink">{seats.used}</b> of {seats.limit} seats</span>
          {seats.pendingInvites > 0 && <span className="tabular">{seats.pendingInvites} invited</span>}
          <span className="tabular">{seats.included} free + {seats.paid} paid</span>
          {over > 0 && <span className="text-danger font-semibold">{over} over limit</span>}
        </div>
      )}
    </div>
  )
}

/** Field pair. Blank renders a dash — never an empty gap the reviewer has to
 *  guess at. */
function Field({ label, value, mono = false }: { label: string; value?: string | number | null; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-soft font-semibold">{label}</div>
      <div className={`text-ink truncate ${mono ? 'tabular' : ''}`} title={value != null ? String(value) : undefined}>
        {value == null || value === '' ? '—' : String(value)}
      </div>
    </div>
  )
}

/**
 * The flag notice. This is not decoration: with `ORGS_LIVE` off nothing on the
 * platform reads an org, and with `ORG_KYC_LIVE` off a decision is recorded but
 * no member inherits it. A reviewer working a queue whose decisions do nothing
 * has to be told, at the top, every time.
 */
function GateBanner({ gate }: { gate: AdminOrgGate | null }) {
  if (!gate || gate.inheritanceActive || !gate.notice) return null
  return (
    <div
      className="flex items-start gap-2.5 rounded-md border border-warning/30 bg-warning/[0.07] px-3 py-2.5 text-xs text-ink"
      data-testid="admin-orgs-gate-notice"
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" className="mt-px shrink-0 text-warning">
        <circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" />
      </svg>
      <span>{gate.notice}</span>
    </div>
  )
}

/* ──────────────────────────────── the lens ───────────────────────────────── */

type Chip = { key: string; label: string; status?: KycStatus }
const CHIPS: Chip[] = [
  { key: 'submitted', label: 'Awaiting review', status: 'submitted' },
  { key: 'all', label: 'All' },
  { key: 'approved', label: 'Verified', status: 'approved' },
  { key: 'rejected', label: 'Rejected', status: 'rejected' },
  { key: 'not_submitted', label: 'No documents', status: 'not_submitted' },
]

export function AdminOrgs({ focusOrgId, onOpenPerson }: {
  /** Deep-link from the People lens: open this company's panel on mount. */
  focusOrgId?: string | null
  /** Jump back to a person from the members table. */
  onOpenPerson?: (uid: string) => void
}) {
  const reduce = useReducedMotion()
  const [rows, setRows] = useState<AdminOrgRow[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [gate, setGate] = useState<AdminOrgGate | null>(null)
  const [chip, setChip] = useState<string>('submitted')
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(focusOrgId ?? null)
  // Tombstoned orgs (owner account deleted) are NOISE wearing a company name —
  // dad flagged markup on a purged QA twin because one sat in this queue
  // looking decidable. Hidden unless explicitly shown.
  const [showOrphaned, setShowOrphaned] = useState(false)
  const [orphanedCount, setOrphanedCount] = useState(0)
  const searchRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    try {
      const r = await adminApi.orgs.list({ limit: 200, sort: 'review', includeOrphaned: showOrphaned })
      setRows(r.orgs)
      setCounts(r.counts || {})
      setOrphanedCount(r.orphanedCount ?? 0)
      setGate(r.gate ?? null)
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : ((e as Error)?.message || 'Could not load organizations'))
    } finally { setLoading(false) }
  }, [showOrphaned])

  useEffect(() => { load() }, [load])
  useEffect(() => { if (focusOrgId) { setChip('all'); setOpen(focusOrgId) } }, [focusOrgId])

  // `/` focuses search. Dad works this queue daily; reaching for the mouse to
  // type a company name is the difference between a tool and a form.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey) return
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const status = CHIPS.find((c) => c.key === chip)?.status
    return rows.filter((r) => {
      if (status && r.kycStatus !== status) return false
      if (!needle) return true
      return [r.legalName, r.displayName, r.id, r.merchantId, r.countryCode, String(r.legacyUserId ?? '')]
        .some((v) => !!v && String(v).toLowerCase().includes(needle))
    })
  }, [rows, chip, q])

  if (loading) return <div className="card animate-pulse h-64" data-testid="admin-orgs" />
  if (error) {
    return (
      <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-4" data-testid="admin-orgs">
        {error}
      </div>
    )
  }

  return (
    <div className="space-y-3" data-testid="admin-orgs">
      <GateBanner gate={gate} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {CHIPS.map((c) => {
            const n = c.status ? (counts[c.status] ?? 0) : (counts.total ?? rows.length)
            const active = chip === c.key
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setChip(c.key)}
                data-testid={`admin-orgs-chip-${c.key}`}
                className={`relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active ? 'text-white' : 'text-navy-500 hover:text-ink'
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="admin-orgs-chip"
                    className="absolute inset-0 rounded-full bg-navy-900"
                    transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 34 }}
                  />
                )}
                <span className="relative">{c.label}</span>
                <span className={`relative tabular text-[10px] ${active ? 'text-white/70' : 'text-soft'}`}>{n}</span>
              </button>
            )
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {(orphanedCount > 0 || showOrphaned) && (
            <button
              type="button"
              onClick={() => setShowOrphaned((v) => !v)}
              className={`whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                showOrphaned ? 'border-navy-300 bg-page text-ink' : 'border-border text-soft hover:text-ink'
              }`}
              data-testid="admin-orgs-orphaned-toggle"
              title="Orgs whose owner account was deleted — decisions on them apply to nobody"
            >
              {showOrphaned ? 'Hide orphaned' : `Show orphaned (${orphanedCount})`}
            </button>
          )}
          <input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Company, org id, merchant… (press /)"
            className="h-9 w-56 rounded-sm border border-border bg-paper px-3 text-sm text-ink placeholder:text-soft"
            data-testid="admin-orgs-search"
          />
          <span className="text-xs text-muted whitespace-nowrap tabular">{visible.length} shown</span>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="card text-sm text-muted" data-testid="admin-orgs-empty">
          {rows.length === 0
            ? 'No organizations yet. They appear once the org backfill has run (scripts/ops/backfill-orgs.mjs) or a new account signs up with ORGS_LIVE armed.'
            : 'No organization matches this filter.'}
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          {visible.map((row, i) => (
            <OrgRow
              key={row.id}
              row={row}
              index={i}
              open={open === row.id}
              onToggle={() => setOpen(open === row.id ? null : row.id)}
              onChanged={load}
              onOpenPerson={onOpenPerson}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ──────────────────────────────── one row ────────────────────────────────── */

function OrgRow({ row, index, open, onToggle, onChanged, onOpenPerson }: {
  row: AdminOrgRow
  index: number
  open: boolean
  onToggle: () => void
  onChanged: () => void
  onOpenPerson?: (uid: string) => void
}) {
  const reduce = useReducedMotion()
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index, 8) * 0.018, ease: [0.16, 1, 0.3, 1] }}
      className="border-b border-line last:border-b-0"
      data-testid={`admin-org-${row.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${open ? 'bg-cream' : 'hover:bg-cream/60'}`}
        data-testid="admin-org-row"
      >
        <motion.svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-soft"
          animate={{ rotate: open ? 90 : 0 }} transition={reduce ? { duration: 0 } : { duration: 0.18 }}
        >
          <path d="m9 18 6-6-6-6" />
        </motion.svg>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-ink truncate">{row.displayName || row.legalName}</span>
            <span className="rounded-full bg-page px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-navy-500">
              {row.kind === 'broker' ? 'Broker' : 'Company'}
            </span>
            {row.tradeRole && (
              <span className="text-[10px] uppercase tracking-wider text-soft">{row.tradeRole}</span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted">
            <span className="truncate">{row.legalName}</span>
            {row.countryCode && <span className="tabular">· {row.countryCode}</span>}
            {row.merchantId && <span className="tabular">· merchant {row.merchantId}</span>}
            {/* Twin disambiguator: two orgs can share a name (a re-created
                account mints a fresh solo org) — the created date is what
                tells them apart at a glance. */}
            {row.createdAt && <span className="tabular whitespace-nowrap">· since {fmtDate(row.createdAt)}</span>}
          </div>
        </div>
        <div className="hidden sm:block"><SeatMeter seats={row.seats} compact /></div>
        {row.orphaned && (
          <span
            className="rounded-full bg-page px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-soft"
            title="The owner's account was deleted — KYC/markup/seat decisions on this org apply to nobody"
            data-testid="admin-org-orphaned-pill"
          >
            orphaned
          </span>
        )}
        {row.markup?.enabled && (
          <span
            className="rounded-full bg-gold-050 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gold-600"
            title={`Markup mode: members browse at +${row.markup.percent ?? 15}% with Aska as the supplier contact`}
            data-testid="admin-org-markup-pill"
          >
            +{row.markup.percent ?? 15}% markup
          </span>
        )}
        <span className="hidden md:block w-24 text-right text-xs text-muted tabular">
          {row.documentCount > 0 ? `${row.documentCount} doc${row.documentCount === 1 ? '' : 's'}` : 'no docs'}
        </span>
        <StatusPill status={row.kycStatus} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden bg-cream/60"
          >
            <OrgPanel orgId={row.id} onChanged={onChanged} onOpenPerson={onOpenPerson} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

/* ────────────────────────────── review panel ─────────────────────────────── */

function OrgPanel({ orgId, onChanged, onOpenPerson }: {
  orgId: string
  onChanged: () => void
  onOpenPerson?: (uid: string) => void
}) {
  const [data, setData] = useState<AdminOrgDetailResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [customPct, setCustomPct] = useState('')

  const load = useCallback(async () => {
    try {
      setData(await adminApi.orgs.detail(orgId))
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : ((e as Error)?.message || 'Could not load this organization'))
    }
  }, [orgId])
  useEffect(() => { load() }, [load])

  async function openDoc(objectPath: string) {
    // Open the tab synchronously so the user-gesture isn't consumed by the
    // await below — a popup opened AFTER the fetch gets blocked by the browser.
    const win = window.open('', '_blank', 'noopener')
    try {
      const { url } = await adminApi.kycDocUrl(objectPath)
      if (win) win.location.href = url
      else window.location.href = url
    } catch (e) {
      if (win) win.close()
      setError(e instanceof ApiError ? e.message : ((e as Error)?.message || 'Could not open document'))
    }
  }

  async function decide(approve: boolean) {
    setBusy('kyc'); setFlash(null); setError(null)
    try {
      const r = await adminApi.orgs.kyc(orgId, approve, approve ? undefined : (reason.trim() || undefined))
      setRejecting(false); setReason('')
      const inherits = data?.org.gate.inheritanceActive
      const people = r.membersInvalidated
      // NEVER a bare success. Say what moved, for how many people, and when
      // the rest of the fleet catches up — or say plainly that nothing moved.
      if (!inherits) {
        setFlash({
          tone: 'warn',
          text: `Recorded ${approve ? 'approval' : 'rejection'} on ${data?.org.legalName ?? 'this organization'}. `
            + 'No member’s access changed — org-inherited verification is not armed on this deployment.',
        })
      } else if (approve) {
        setFlash({
          tone: 'ok',
          text: `Verified. ${people} member${people === 1 ? '' : 's'} inherit prices, supplier contact and stock references — `
            + `immediately here, within ${r.propagationSlaSeconds}s everywhere.`,
        })
      } else {
        setFlash({
          tone: 'warn',
          text: `Rejected. This locks all ${people} active member${people === 1 ? '' : 's'}, overriding their own approvals, `
            + 'and there is no per-person way back out — re-approving the company is the only remedy.',
        })
      }
      await load()
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : ((e as Error)?.message || 'Decision failed'))
    } finally { setBusy(null) }
  }

  async function grantSeats(paid: number) {
    setBusy('seats'); setFlash(null); setError(null)
    try {
      const r = await adminApi.orgs.grantSeats(orgId, paid, 'Granted from the admin console')
      setFlash({
        tone: r.belowUsage ? 'warn' : 'ok',
        text: r.warning || `Seats set to ${r.seats.limit} (${r.seats.included} free + ${r.seats.paid} granted).`,
      })
      await load()
      onChanged()
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : ((e as Error)?.message || 'Seat grant failed')
      setError(e instanceof ApiError && e.status === 404
        ? 'Seat grants are refused on this deployment — organizations are not armed (ORGS_LIVE is off).'
        : msg)
    } finally { setBusy(null) }
  }

  /** Per-member seat control = the per-person KYC writer (the designed
   *  suspend/restore mechanism for one seat in a multi-member company). */
  async function memberSeat(uid: string, restore: boolean) {
    setBusy(`seat-${uid}`); setFlash(null); setError(null)
    try {
      const r = await adminApi.kyc(uid, restore, restore ? undefined : 'Seat revoked from the admin console')
      setFlash({
        tone: restore ? 'ok' : 'warn',
        text: restore
          ? 'Seat restored — they inherit the company verification again within 60s.'
          : `Seat revoked — this person browses with locked prices until restored.${r.warning ? ` ${r.warning}` : ''}`,
      })
      await load()
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : ((e as Error)?.message || 'Seat change failed'))
    } finally { setBusy(null) }
  }

  async function removeMember(uid: string, label: string) {
    if (!window.confirm(`Remove ${label} from this company? They keep their own account and personal KYC — they just stop inheriting this company's verification and seat.`)) return
    setBusy(`remove-${uid}`); setFlash(null); setError(null)
    try {
      const r = await adminApi.orgs.removeMember(orgId, uid)
      setFlash({ tone: 'ok', text: `${label} removed from the company${r.freedSeat ? ' — their seat is free again' : ''}.` })
      await load()
      onChanged()
    } catch (e) {
      const err = e as ApiError
      setError(err instanceof ApiError && (err.body as { code?: string } | undefined)?.code === 'owner_not_removable'
        ? 'This person OWNS the company — transfer ownership first, or delete their account from the People lens.'
        : (e instanceof ApiError ? e.message : ((e as Error)?.message || 'Removal failed')))
    } finally { setBusy(null) }
  }

  async function setMarkup(enabled: boolean, percent?: number) {
    setBusy('markup'); setFlash(null); setError(null)
    try {
      const r = await adminApi.orgs.markup(orgId, enabled, percent)
      const people = r.membersInvalidated
      // Name WHO this lands on — two orgs can share a display name (the
      // 2026-09-20 mis-flag), so the flash carries the owner's email + the
      // org's created date, the two facts that tell twins apart.
      const owner = data?.org.members.find((m) => m.isOwner)?.email
        ?? data?.org.ownerUid ?? 'unknown owner'
      const since = data?.org.createdAt ? ` · since ${fmtDate(data.org.createdAt)}` : ''
      const who = `${data?.org.legalName ?? 'this organization'} (owner ${owner}${since})`
      // The same honesty rule as the KYC decision: recorded-but-inert is a
      // warning, never a plain success.
      if (r.orphaned) {
        setFlash({
          tone: 'warn',
          text: `Recorded on ${who} — but this org is ORPHANED: its owner's account was deleted, so nobody can ever see this markup. You probably meant its living twin.`,
        })
      } else if (!r.live) {
        setFlash({
          tone: 'warn',
          text: `Recorded — markup ${enabled ? `set to ${r.effectivePercent}%` : 'turned off'} on ${who}, `
            + 'but markup mode is NOT armed on this deployment (ORG_MARKUP_LIVE is off), so every member still sees the real catalog.',
        })
      } else if (enabled) {
        setFlash({
          tone: 'ok',
          text: `Markup on for ${who}. ${people} member${people === 1 ? '' : 's'} now browse at ${r.effectivePercent}% over the supplier price `
            + `with Aska as the contact on every stone — immediately here, within ${r.propagationSlaSeconds}s everywhere.`,
        })
      } else {
        setFlash({
          tone: 'ok',
          text: `Markup off for ${who}. ${people} member${people === 1 ? '' : 's'} see real supplier prices and contacts again within ${r.propagationSlaSeconds}s.`,
        })
      }
      await load()
      onChanged()
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404
        ? 'Markup changes are refused on this deployment — organizations are not armed (ORGS_LIVE is off).'
        : (e instanceof ApiError ? e.message : ((e as Error)?.message || 'Markup change failed')))
    } finally { setBusy(null) }
  }

  if (error && !data) return <div className="px-4 py-4 text-sm text-danger">{error}</div>
  if (!data) return <div className="px-4 py-6"><div className="h-24 animate-pulse rounded-md bg-page" /></div>

  const org = data.org
  const drift = data.seatsStored.used !== org.seats.used
  const reg = Object.entries(org.registrationNumbers)
  // Custom markup: the server accepts any integer 1–500 (admin/orgs MarkupSchema),
  // so the preset chips are just shortcuts — this validates a free-typed percent.
  const customPctNum = Number(customPct)
  const customPctValid = customPct.trim() !== ''
    && Number.isInteger(customPctNum) && customPctNum >= 1 && customPctNum <= 500
  const applyCustomMarkup = () => { if (customPctValid) { void setMarkup(true, customPctNum); setCustomPct('') } }

  return (
    <div className="space-y-4 px-4 py-4" data-testid={`admin-org-panel-${orgId}`}>
      {error && <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">{error}</div>}
      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`rounded-md border px-3 py-2 text-xs ${
              flash.tone === 'ok' ? 'border-success/30 bg-success/10 text-success' : 'border-warning/40 bg-warning/10 text-ink'
            }`}
            data-testid="admin-org-flash"
          >
            {flash.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Decision bar. The only place a company can be verified. ── */}
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-paper px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-soft font-semibold">Verification</div>
          <div className="text-sm text-ink">
            <StatusPill status={org.kycStatus} />
            <span className="ml-2 text-xs text-muted">
              {org.kycStatus === 'submitted' && org.kycSubmittedAt ? `submitted ${fmtDate(org.kycSubmittedAt)}`
                : org.kycReviewedAt ? `reviewed ${fmtDate(org.kycReviewedAt)}` : 'never reviewed'}
              {org.kycResubmittedAt ? ` · new documents ${fmtDate(org.kycResubmittedAt)}` : ''}
            </span>
          </div>
          {org.kycRejectionReason && (
            <div className="mt-1 text-xs text-danger">Last rejection: {org.kycRejectionReason}</div>
          )}
        </div>
        {!rejecting ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={busy === 'kyc'}
              onClick={() => decide(true)}
              className="rounded-md bg-success/90 px-3 py-1.5 text-xs font-semibold text-white hover:bg-success disabled:opacity-50"
              data-testid="admin-org-approve"
            >
              {busy === 'kyc' ? '…' : org.kycApproved ? 'Re-approve company' : 'Approve company'}
            </button>
            <button
              type="button"
              disabled={busy === 'kyc'}
              onClick={() => setRejecting(true)}
              className="rounded-md border border-danger bg-paper px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/5 disabled:opacity-50"
              data-testid="admin-org-reject"
            >
              Reject company
            </button>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} className="flex items-center gap-2">
            <input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (emailed to the owner)"
              className="h-8 w-64 rounded-sm border border-border bg-paper px-2 text-xs text-ink"
              data-testid="admin-org-reject-reason"
            />
            <button
              type="button"
              disabled={busy === 'kyc'}
              onClick={() => decide(false)}
              className="rounded-md bg-danger px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
              data-testid="admin-org-reject-confirm"
            >
              {busy === 'kyc' ? '…' : `Reject — locks ${org.members.filter((m) => m.status === 'active').length}`}
            </button>
            <button type="button" onClick={() => { setRejecting(false); setReason('') }}
              className="text-xs text-muted hover:text-ink">Cancel</button>
          </motion.div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Company ── */}
        <section className="rounded-md border border-line bg-paper p-3">
          <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-soft">Company</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Field label="Legal name" value={org.legalName} />
            <Field label="Trading as" value={org.displayName} />
            <Field label="Kind" value={org.kind === 'broker' ? 'Individual broker' : 'Company'} />
            <Field label="Trades as" value={org.tradeRole} />
            <Field label="Website" value={org.website} />
            <Field label="Billing country" value={org.billingCountryCode || org.countryCode} />
            <Field label="Heard via" value={org.hearAbout} />
            <Field label="Created" value={`${fmtDate(org.createdAt)}${org.createdFrom ? ` · ${org.createdFrom}` : ''}`} />
            <Field label="Org id" value={org.id} mono />
            <Field label="Merchant / legacy" value={`${org.merchantId ?? '—'} / ${org.legacyUserId ?? '—'}`} mono />
          </div>
          {reg.length > 0 && (
            <div className="mt-3">
              <h5 className="mb-1 text-[10px] font-bold uppercase tracking-wider text-soft">Registration numbers</h5>
              <div className="flex flex-wrap gap-1.5">
                {reg.map(([k, v]) => (
                  <span key={k} className="pill text-xs tabular">
                    <span className="text-[9px] font-bold uppercase text-gold-600">{k}</span>{v}
                  </span>
                ))}
              </div>
            </div>
          )}
          {org.supplierContact && (
            <div className="mt-3 text-xs text-muted">
              Supplier card: {org.supplierContact.name || '—'} · {org.supplierContact.phone || '—'} · {org.supplierContact.email || '—'}
            </div>
          )}
        </section>

        {/* ── Documents ── */}
        <section className="rounded-md border border-line bg-paper p-3">
          <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-soft">
            Documents{org.kycDocuments.length > 0 ? ` (${org.kycDocuments.length})` : ''}
          </h4>
          {org.kycDocuments.length === 0 ? (
            <p className="text-xs text-muted">
              Nothing uploaded. Approving on no evidence is a decision — make it deliberately.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {org.kycDocuments.map((d) => (
                <button
                  key={d.objectPath}
                  type="button"
                  onClick={() => openDoc(d.objectPath)}
                  className="pill text-xs"
                  title={`Open ${d.fileName}`}
                  data-testid="admin-org-doc"
                >
                  <span className="text-[9px] font-bold uppercase text-gold-600">{tileFor(d.docType).title}</span>
                  {d.fileName}
                </button>
              ))}
            </div>
          )}
          {org.supplierDataSource?.method && (
            <div className="mt-3 border-t border-line pt-2 text-xs">
              <div className="text-[10px] font-bold uppercase tracking-wider text-soft">Stock connection</div>
              <div className="mt-1 text-ink">
                {org.supplierDataSource.method === 'api' ? 'Supplier API'
                  : org.supplierDataSource.method === 'excel' ? 'Excel / CSV sheet'
                  : org.supplierDataSource.method === 'erp_push' ? 'ERP push' : 'Postponed'}
                {org.supplierDataSource.api?.url ? ` · ${org.supplierDataSource.api.url}` : ''}
                {/* The credential is never sent to this console — only whether
                    one is on file, and its last 4. */}
                {org.supplierDataSource.api
                  ? (org.supplierDataSource.api.hasKey
                    ? ` · key ${org.supplierDataSource.api.keyMasked}`
                    : ' · NO KEY ON FILE')
                  : ''}
              </div>
            </div>
          )}
        </section>

        {/* ── Seats + billing ── */}
        <section className="rounded-md border border-line bg-paper p-3">
          <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-soft">Seats &amp; billing</h4>
          <SeatMeter seats={org.seats} />
          {drift && (
            <p className="mt-2 text-[11px] text-warning">
              The stored counter says {data.seatsStored.used} used; recounted from the member rows it is {org.seats.used}.
              The recount is what the seat transactions enforce — this row just needs a write to settle.
            </p>
          )}
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Field label="Subscription" value={org.billing.subscriptionStatus} />
            <Field label="Source" value={org.billing.source} />
            <Field label="Stripe quantity" value={org.billing.quantity} mono />
            <Field label="Renews" value={fmtDate(org.billing.currentPeriodEnd)} />
          </div>
          <div className="mt-3 border-t border-line pt-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-soft">Grant seats</div>
            <p className="mt-0.5 text-[11px] text-muted">
              India is bank-transfer only and there is no recurring rail, so paid seats there are granted by hand.
              This is the TOTAL granted, not an increment — pressing twice grants once.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {[0, 1, 2, 5, 7, 17].map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={busy === 'seats' || org.seats.paid === n}
                  onClick={() => grantSeats(n)}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-40 ${
                    org.seats.paid === n
                      ? 'bg-navy-900 text-white'
                      : 'border border-border bg-paper text-navy-500 hover:border-navy-300 hover:text-ink'
                  }`}
                  data-testid={`admin-org-grant-${n}`}
                  title={`${org.seats.included + n} seats total`}
                >
                  {n === 0 ? 'none' : `+${n}`}
                </button>
              ))}
              <span className="text-[11px] text-muted tabular">
                → {org.seats.included + org.seats.paid} seats
              </span>
            </div>
          </div>
          <div className="mt-3 border-t border-line pt-3" data-testid="admin-org-markup">
            <div className="flex items-center gap-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-soft">Markup mode</div>
              {org.markup?.enabled && (
                <span className="rounded-full bg-gold-050 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gold-600">
                  {org.markup.percent != null ? `+${org.markup.percent}%` : 'on'}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[11px] text-muted">
              On ⇒ every verified member of this company browses at a marked-up price (default +15%)
              and the contact card on every stone shows Aska instead of the supplier — supplier names,
              stock references and raw prices are hidden from them everywhere. Buyers are never told.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {!org.markup?.enabled ? (
                <>
                  <button
                    type="button"
                    disabled={busy === 'markup'}
                    onClick={() => setMarkup(true)}
                    className="rounded-md bg-navy-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy-700 disabled:opacity-50"
                    data-testid="admin-org-markup-on"
                  >
                    {busy === 'markup' ? '…' : 'Turn on (+15%)'}
                  </button>
                  {[10, 20, 25].map((p) => (
                    <button
                      key={p}
                      type="button"
                      disabled={busy === 'markup'}
                      onClick={() => setMarkup(true, p)}
                      className="rounded-full border border-border bg-paper px-2.5 py-1 text-xs font-semibold text-navy-500 hover:border-navy-300 hover:text-ink disabled:opacity-40"
                      data-testid={`admin-org-markup-on-${p}`}
                    >
                      +{p}%
                    </button>
                  ))}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={busy === 'markup'}
                    onClick={() => setMarkup(false)}
                    className="rounded-md border border-danger/40 bg-paper px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/5 disabled:opacity-50"
                    data-testid="admin-org-markup-off"
                  >
                    {busy === 'markup' ? '…' : 'Turn off'}
                  </button>
                  {[10, 15, 20, 25].filter((p) => p !== (org.markup?.percent ?? 15)).map((p) => (
                    <button
                      key={p}
                      type="button"
                      disabled={busy === 'markup'}
                      onClick={() => setMarkup(true, p)}
                      className="rounded-full border border-border bg-paper px-2.5 py-1 text-xs font-semibold text-navy-500 hover:border-navy-300 hover:text-ink disabled:opacity-40"
                    >
                      switch to +{p}%
                    </button>
                  ))}
                </>
              )}
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              <label htmlFor={`admin-org-markup-custom-${orgId}`} className="text-[11px] text-muted">
                Custom
              </label>
              <input
                id={`admin-org-markup-custom-${orgId}`}
                type="number"
                min={1}
                max={500}
                step={1}
                value={customPct}
                onChange={(e) => setCustomPct(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyCustomMarkup() }}
                disabled={busy === 'markup'}
                placeholder="%"
                className="w-16 rounded-md border border-border bg-paper px-2 py-1 text-xs tabular text-ink placeholder:text-soft focus:border-navy-300 focus:outline-none disabled:opacity-40"
                data-testid="admin-org-markup-custom-input"
              />
              <button
                type="button"
                disabled={busy === 'markup' || !customPctValid}
                onClick={applyCustomMarkup}
                className="rounded-full border border-navy-300 bg-paper px-2.5 py-1 text-xs font-semibold text-navy-600 hover:border-navy-500 hover:text-ink disabled:opacity-40"
                data-testid="admin-org-markup-custom-apply"
              >
                {customPctValid ? `Apply +${customPctNum}%` : 'Apply'}
              </button>
            </div>
          </div>
        </section>

        {/* ── People ── */}
        <section className="rounded-md border border-line bg-paper p-3">
          <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-soft">
            People ({data.activeMemberCount} active of {data.memberCount})
          </h4>
          {org.members.length === 0 ? (
            <p className="text-xs text-muted">No membership rows. Verification cannot be inherited by anyone.</p>
          ) : (
            <div className="-mx-1 overflow-x-auto">
              <table className="w-full text-xs">
                <tbody>
                  {org.members.map((m) => (
                    <tr key={m.uid} className="border-b border-line last:border-b-0" data-testid={`admin-org-member-${m.uid}`}>
                      <td className="py-1.5 pr-2">
                        <button
                          type="button"
                          onClick={() => onOpenPerson?.(m.uid)}
                          className="text-left font-medium text-ink hover:text-gold-600"
                        >
                          {m.displayName || m.email || m.uid}
                        </button>
                        {m.email && m.displayName && <div className="text-[11px] text-muted">{m.email}</div>}
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-navy-500">
                          {m.isOwner ? 'owner' : m.orgRole || 'member'}
                        </span>
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        {m.status === 'active'
                          ? <span className="text-success">active</span>
                          : <span className="text-warning" title={m.suspendedReason || undefined}>
                              suspended{m.suspendedReason ? ` · ${m.suspendedReason.replace(/_/g, ' ')}` : ''}
                            </span>}
                      </td>
                      <td className="py-1.5 pr-2 whitespace-nowrap text-muted tabular">
                        {m.occupiesSeat ? 'holds a seat' : 'no seat'}
                      </td>
                      {/* Seat documents (collect + review): chips say what's
                          on file; click opens the doc via the signed-URL
                          mint. Missing docs are a REVIEW cue, never a lock. */}
                      <td className="py-1.5 pr-2 whitespace-nowrap">
                        <MemberDocChip
                          label="ID"
                          ok={m.hasIdentityDoc === true}
                          doc={(m.documents ?? []).find((d) => d.docType === 'id' || d.docType === 'passport')}
                        />
                        <MemberDocChip
                          label="Employment"
                          ok={m.hasEmploymentProof === true}
                          doc={(m.documents ?? []).find((d) => d.docType === 'employment_proof')}
                        />
                      </td>
                      <td className="py-1.5 pr-2 text-right whitespace-nowrap text-muted tabular">{fmtDate(m.joinedAt)}</td>
                      {/* Seat controls (Tej 2026-09-20). Revoke = the per-person
                          KYC DENY (suspends exactly this seat, reversible by
                          Restore — the designed mechanism); Remove = off the
                          company, personal KYC kept. Owner rows get neither:
                          the company decision IS the owner's, and removal
                          would orphan the org (transfer or delete-from-Aska). */}
                      <td className="py-1.5 text-right whitespace-nowrap">
                        {!m.isOwner && (
                          <>
                            {m.status === 'active' ? (
                              <button
                                type="button"
                                disabled={busy === `seat-${m.uid}`}
                                onClick={() => memberSeat(m.uid, false)}
                                className="mr-1 rounded-md border border-warning/50 bg-paper px-2 py-1 text-[11px] font-semibold text-warning hover:bg-warning/10 disabled:opacity-50"
                                data-testid={`admin-member-revoke-${m.uid}`}
                                title="Suspend this seat (person-level KYC deny) — they browse locked until restored"
                              >
                                Revoke seat
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={busy === `seat-${m.uid}`}
                                onClick={() => memberSeat(m.uid, true)}
                                className="mr-1 rounded-md border border-success/50 bg-paper px-2 py-1 text-[11px] font-semibold text-success hover:bg-success/10 disabled:opacity-50"
                                data-testid={`admin-member-restore-${m.uid}`}
                              >
                                Restore
                              </button>
                            )}
                            <button
                              type="button"
                              disabled={busy === `remove-${m.uid}`}
                              onClick={() => removeMember(m.uid, m.displayName || m.email || m.uid)}
                              className="rounded-md border border-danger/50 bg-paper px-2 py-1 text-[11px] font-semibold text-danger hover:bg-danger/5 disabled:opacity-50"
                              data-testid={`admin-member-remove-${m.uid}`}
                              title="Remove from this company — their personal KYC is kept"
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {org.invites.length > 0 && (
            <div className="mt-2 border-t border-line pt-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-soft">Invites</div>
              {org.invites.map((i) => (
                <div key={i.inviteId} className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-ink">{i.email} · {i.orgRole}</span>
                  <span className={i.expired ? 'text-warning' : 'text-muted'}>
                    {i.status}{i.expiresAt ? ` · ${i.expired ? 'expired' : 'expires'} ${fmtDate(i.expiresAt)}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {org.membersTruncated && (
            <p className="mt-2 text-[11px] text-warning">More rows than this panel reads — the list is truncated.</p>
          )}
        </section>
      </div>

      {/* ── Devices ── */}
      <section className="rounded-md border border-line bg-paper p-3">
        <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-soft">Device sessions</h4>
        {org.devices.length === 0 ? (
          <p className="text-xs text-muted">
            No registered devices. The device registry is written by the sign-in path; nothing here means nobody in this
            company has signed in since it shipped.
          </p>
        ) : (
          <div className="space-y-1.5">
            {org.devices.map((d) => (
              <div key={d.uid} className="flex flex-wrap items-baseline gap-x-3 text-xs">
                <span className="font-medium text-ink">
                  {org.members.find((m) => m.uid === d.uid)?.email || d.uid}
                </span>
                <span className="text-muted tabular">{d.activeCount} active{d.limit ? ` of ${d.limit}` : ''}</span>
                {d.exempt && <span className="text-[10px] uppercase tracking-wider text-warning">exempt</span>}
                <span className="text-muted truncate">
                  {d.devices.map((x) => `${x.label || x.platform || 'device'}${x.status === 'evicted' ? ' (signed out)' : ''}`).join(' · ')}
                </span>
                <span className="text-soft tabular">
                  {fmtDateTime(d.devices.map((x) => x.lastSeenAt).sort().reverse()[0])}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default AdminOrgs


/** One seat-document chip: green when on file (click to open via the signed
 *  read URL), amber "needed" when absent. */
function MemberDocChip({ label, ok, doc }: {
  label: string
  ok: boolean
  doc?: { fileName: string; objectPath: string }
}) {
  async function open() {
    if (!doc) return
    const win = window.open('', '_blank', 'noopener')
    try {
      const { url } = await adminApi.kycDocUrl(doc.objectPath)
      if (win) win.location.href = url
      else window.location.href = url
    } catch { win?.close() }
  }
  return (
    <button
      type="button"
      onClick={open}
      disabled={!doc}
      title={doc ? doc.fileName : `No ${label.toLowerCase()} document on file`}
      className={`mr-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        ok ? 'bg-success/15 text-success hover:bg-success/25' : 'bg-warning/15 text-warning cursor-default'
      }`}
    >
      {label} {ok ? '✓' : '·'}
    </button>
  )
}
